/**
 * Phone-number / WhatsApp JID masking helpers.
 *
 * Used by usePhonePrivacy() to redact contact phone numbers shown to
 * staff operators when a tenant's privacy toggle is enabled.
 *
 * Design choices:
 *  - The masked rendering is fixed-width: `<first4>****<last4>`.
 *    We deliberately do NOT preserve the original length, because that
 *    leaks information about the number's country and would let an
 *    operator reconstruct most of it.
 *  - Group / broadcast / channel JIDs are passed through to the caller
 *    by isGroupJid() — they are not phone numbers and should be
 *    labelled by their name elsewhere.
 *  - Strings shorter than 9 digits are returned (post-strip) as-is.
 *    The mask format requires 4 + 4 visible digits, so anything below
 *    that is left untouched rather than rendered as gibberish.
 */

const JID_SUFFIX_RE = /@(s\.whatsapp\.net|g\.us|lid|broadcast|newsletter)$/i

/**
 * Returns true when the input is a WhatsApp group-like JID. These are
 * not personal phone numbers and must not be masked — callers should
 * display the group name (or a generic "Grupo" placeholder) instead.
 */
export function isGroupJid(input: string | null | undefined): boolean {
  if (typeof input !== 'string' || input.length === 0) return false
  return input.endsWith('@g.us') || input.endsWith('@broadcast') || input.endsWith('@newsletter')
}

/**
 * Strip a WhatsApp JID suffix (`@s.whatsapp.net`, `@g.us`, `@lid`,
 * `@broadcast`, `@newsletter`) from the input. Returns the local part
 * unchanged when no suffix is present.
 *
 * Does NOT strip non-digit characters — that's maskPhone's job, because
 * raw display sometimes wants to keep the `+` for readability.
 */
export function stripJidSuffix(input: string | null | undefined): string {
  if (typeof input !== 'string') return ''
  return input.replace(JID_SUFFIX_RE, '')
}

/**
 * Mask a phone number / JID for display.
 *
 * Examples:
 *   "5215549856040@s.whatsapp.net" -> "5215****6040"
 *   "+5511999998888"               -> "5511****8888"
 *   "554196558044"                 -> "5541****8044"
 *   "12"                           -> "12"     (too short)
 *   "120363347787801853@g.us"      -> ""       (group — caller handles label)
 *   ""                             -> ""
 */
export function maskPhone(input: string | null | undefined): string {
  if (typeof input !== 'string' || input.length === 0) return ''

  // Group-like JIDs are not phone numbers — return empty so the caller
  // falls back to whatever group-name logic it owns.
  if (isGroupJid(input)) return ''

  // Drop JID suffix first, then anything that isn't a digit. The `+`,
  // spaces, hyphens and parentheses commonly seen in CRM-entered phones
  // are noise as far as the mask is concerned.
  const digits = stripJidSuffix(input).replace(/\D/g, '')

  // Below 9 digits the 4-star-4 pattern would expose more of the number
  // than it hides. Return the cleaned digits untouched.
  if (digits.length < 9) return digits

  return digits.slice(0, 4) + '****' + digits.slice(-4)
}
